import { createServerFn } from "@tanstack/react-start";
import { format } from "date-fns";

/** Normalized article shape shared by static and CMS posts. */
export type CmsArticleView = {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  isoDate: string;
  updatedIsoDate?: string;
  category: string;
  coverImage?: string;
  coverImageAlt?: string;
  metaTitle: string;
  metaDescription: string;
  contentHtml: string;
  tags: { name: string; slug: string }[];
};

/**
 * Fetch a published CMS post by slug and normalize it so /articles/{slug} can
 * render it with the same head/canonical logic as the static posts.
 */
export const fetchCmsArticle = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => input)
  .handler(async ({ data }): Promise<{ post: CmsArticleView | null }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: post, error } = await supabaseAdmin
      .from("posts")
      .select(
        "title, slug, excerpt, content_html, featured_image, published_at, updated_at, seo_title, seo_description, categories(name), post_tags(tags(name, slug))",
      )
      .eq("slug", data.slug)
      .eq("status", "published")
      .lte("published_at", new Date().toISOString())
      .maybeSingle();
    if (error) throw error;
    if (!post) return { post: null };

    const isoDate = post.published_at ?? post.updated_at ?? new Date().toISOString();
    const excerpt = post.excerpt ?? "";

    return {
      post: {
        slug: post.slug,
        title: post.title,
        excerpt,
        author: "SimpleTech Team",
        date: format(new Date(isoDate), "PPP"),
        isoDate,
        updatedIsoDate: post.updated_at ?? isoDate,
        category: post.categories?.name ?? "Managed IT",
        coverImage: post.featured_image ?? undefined,
        coverImageAlt: post.title,
        metaTitle: post.seo_title || post.title,
        metaDescription: post.seo_description || excerpt,
        contentHtml: post.content_html ?? "",
        tags: (post.post_tags ?? []).map(
          (pt: { tags: { name: string; slug: string } }) => pt.tags,
        ),
      },
    };
  });

/** Slugs of all published CMS posts — used by the sitemap. */
export const listCmsArticleSlugs = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("posts")
    .select("slug, published_at, updated_at")
    .eq("status", "published")
    .order("published_at", { ascending: false });
  if (error) throw error;
  return { posts: data ?? [] };
});
